import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Video } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { captureWalkthroughThumbnailFromFile } from '../lib/captureWalkthroughThumbnail';
import { detectVideoRotationFromFile } from '../lib/detectWalkthroughVideoRotation';
import {
  resolveTaggedRetailerLocations,
  type RetailerLocationOption,
} from '../lib/inStoreLocationLabels';

interface WalkthroughUploadPanelProps {
  storeId: number;
  storeIdParam: string;
  retailerInstoreLocations: RetailerLocationOption[];
  onUploaded?: () => void;
  /** Pre-select retailer location IDs when opened from a filtered view */
  initialInstoreLocationIds?: number[];
}

const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov';

export function WalkthroughUploadPanel({
  storeId,
  storeIdParam,
  retailerInstoreLocations,
  onUploaded,
  initialInstoreLocationIds = [],
}: WalkthroughUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [routeDescription, setRouteDescription] = useState('');
  const [captureDate, setCaptureDate] = useState(new Date().toISOString().slice(0, 10));
  const [tagsInput, setTagsInput] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>(initialInstoreLocationIds);
  const [isUploading, setIsUploading] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const sortedLocations = useMemo(
    () => [...retailerInstoreLocations].sort((a, b) => a.name.localeCompare(b.name)),
    [retailerInstoreLocations],
  );

  const invalidateQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'context'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/store-profiles', storeIdParam, 'walkthroughs'] }),
      queryClient.invalidateQueries({ queryKey: ['/api/stores', storeIdParam, 'detail'] }),
    ]);
  };

  const toggleLocation = (locationId: number, checked: boolean) => {
    setSelectedLocationIds((current) => {
      if (checked) {
        return current.includes(locationId) ? current : [...current, locationId];
      }
      return current.filter((value) => value !== locationId);
    });
  };

  const handleUpload = async (file: File) => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast({ title: 'Title required', description: 'Give this walk-through a descriptive title.', variant: 'destructive' });
      return;
    }
    if (selectedLocationIds.length === 0) {
      toast({
        title: 'Tag at least one location',
        description: 'Select from the retailer in-store location catalog.',
        variant: 'destructive',
      });
      return;
    }

    setIsUploading(true);
    try {
      const rotationDegrees = await detectVideoRotationFromFile(file).catch(() => 0 as const);
      const thumbnailBlob = await captureWalkthroughThumbnailFromFile(file, rotationDegrees);

      const formData = new FormData();
      formData.append('video', file);
      formData.append('title', trimmedTitle);
      formData.append('videoRotationDegrees', String(rotationDegrees));
      if (thumbnailBlob && thumbnailBlob.size > 0) {
        formData.append(
          'thumbnail',
          new File([thumbnailBlob], 'thumbnail.jpg', { type: 'image/jpeg' }),
        );
      }
      if (routeDescription.trim()) {
        formData.append('routeDescription', routeDescription.trim());
      }
      formData.append('instoreLocationIds', JSON.stringify(selectedLocationIds));
      formData.append('captureDate', captureDate);
      const tags = tagsInput.split(',').map((tag) => tag.trim()).filter(Boolean);
      if (tags.length > 0) {
        formData.append('tags', JSON.stringify(tags));
      }

      const response = await apiRequest(`/api/store-profiles/${storeId}/walkthroughs`, 'POST', formData);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || error.message || 'Upload failed');
      }

      setTitle('');
      setRouteDescription('');
      setCaptureDate(new Date().toISOString().slice(0, 10));
      setTagsInput('');
      setSelectedLocationIds(initialInstoreLocationIds);

      await invalidateQueries();
      onUploaded?.();
      toast({
        title: 'Walk-through uploaded',
        description: [
          `Tagged to ${selectedLocationIds.length} location${selectedLocationIds.length === 1 ? '' : 's'}.`,
          rotationDegrees !== 0 ? `Applied ${rotationDegrees}° orientation fix.` : null,
          !thumbnailBlob ? 'Thumbnail could not be generated in this browser.' : null,
        ].filter(Boolean).join(' '),
      });
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error instanceof Error ? error.message : 'Could not upload walk-through video',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast({ title: 'Invalid file', description: 'Please choose an MP4, WebM, or MOV video.', variant: 'destructive' });
      return;
    }

    await handleUpload(file);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Upload audit video and tag the retailer-defined in-store locations it covers.
        Prefer <strong>MP4</strong> — iPhone MOV may not play in Chrome.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="walk-title">Title</Label>
          <Input
            id="walk-title"
            placeholder="e.g. Front of store audit"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="walk-route">Route description</Label>
          <Input
            id="walk-route"
            placeholder="e.g. Entrance → produce → chill"
            value={routeDescription}
            onChange={(event) => setRouteDescription(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="walk-capture-date">Capture date</Label>
          <Input
            id="walk-capture-date"
            type="date"
            value={captureDate}
            onChange={(event) => setCaptureDate(event.target.value)}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tag in-store locations</Label>
        {sortedLocations.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border rounded-lg p-3">
            {sortedLocations.map((location) => (
              <label key={location.id} className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={selectedLocationIds.includes(location.id)}
                  onCheckedChange={(checked) => toggleLocation(location.id, checked === true)}
                  className="mt-0.5"
                />
                <span className="font-medium">{location.name}</span>
              </label>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No retailer in-store locations defined yet. Add them under retailer lookup tables first.
          </p>
        )}
        {selectedLocationIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {resolveTaggedRetailerLocations(selectedLocationIds, sortedLocations).map((item) => (
              <Badge key={item.id} variant="secondary">
                {item.label}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="walk-tags">Tags (optional)</Label>
        <Input
          id="walk-tags"
          placeholder="audit, baseline, chill"
          value={tagsInput}
          onChange={(event) => setTagsInput(event.target.value)}
        />
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={VIDEO_ACCEPT}
        className="hidden"
        onChange={handleFileChange}
      />
      <Button
        type="button"
        disabled={isUploading || !title.trim() || sortedLocations.length === 0}
        onClick={() => inputRef.current?.click()}
      >
        {isUploading ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <Video className="h-4 w-4 mr-2" />
        )}
        {isUploading ? 'Preparing & uploading…' : 'Choose video & upload'}
      </Button>
    </div>
  );
}
